// src/pages/Account/SostieniCIL.js

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  Text,
  SimpleGrid,
  HStack,
  NumberInput,
  NumberInputField,
  Image,
  Icon,
  Skeleton,
  useToast,
  VStack,
  Container,
} from '@chakra-ui/react';

import {
  FaCheck,
  FaEye,
  FaHandshake,
  FaHeart,
  FaStore,
  FaGift,
  FaGamepad,
  FaCalendarAlt,
  FaTicketAlt,
  FaShoppingBag,
  FaTag,
  FaPaintBrush,
} from 'react-icons/fa';

import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { createSubscription } from '../components/Subscriptions';
import SubscriptionDrawer from '../components/Subscription/SubscriptionDrawer';
import DonationDrawer from '../components/Donations/DonationDrawer';
import HowItWorksModal from '../components/Subscription/HowItWorksModal';
import VantaggiEsclusiviModal from '../components/Subscription/VantaggiEsclusiviModal';
import SostienicilHeroImage from '../images/sostienicil-hero.png';
import {
  C,
  Eyebrow,
  PrimaryButton,
  TextLink,
  Section,
  SectionHead,
} from '../components/ui/SiteKit';

const ACCENTS = [C.yellow, C.green, C.blue, C.pink];

/** Bottone secondario (contorno): per i piani non evidenziati. */
const GhostButton = (props) => (
  <Button
    h="48px"
    px={6}
    bg="transparent"
    color="white"
    fontWeight="800"
    borderRadius="8px"
    boxShadow="inset 0 0 0 1px rgba(255,255,255,0.16)"
    _hover={{ bg: 'rgba(255,255,255,0.06)' }}
    {...props}
  />
);

const IMPACT_STEPS = [
  {
    eq: '50 sostenitori × 5€',
    total: '250€',
    unit: '/mese',
    benefit: 'Montepremi dei tornei Magic da poche decine di euro a 250€.',
    color: C.yellow,
  },
  {
    eq: '25 sostenitori × 5–10€',
    total: '150–250€',
    unit: '/mese',
    benefit:
      'Eventi gratuiti per i soci e spese base coperte (internet, materiali).',
    color: C.green,
  },
  {
    eq: '100 sostenitori × 10€',
    total: '1.000€',
    unit: '/mese',
    benefit: 'Copriamo i costi della sede.',
    color: C.blue,
  },
  {
    eq: '150 sostenitori (mix tier)',
    total: '1.000–1.500€',
    unit: '/mese',
    benefit: 'Copriamo le spese amministrative.',
    color: C.blue,
  },
  {
    eq: '200 sostenitori × 10€',
    total: '2.000€',
    unit: '/mese',
    benefit:
      'Pasti per tutti al centro estivo e prezzi delle attività dimezzati.',
    color: C.pink,
  },
  {
    eq: '300+ sostenitori',
    total: 'Comunità solida',
    unit: '',
    benefit:
      'Progetti educativi stabili, centro estivo accessibile, spazi sempre aperti.',
    color: C.pink,
  },
];

const STORIES = [
  {
    name: 'Marco',
    role: 'Giocatore di Magic',
    text: 'Al prezzo di un caffè alla settimana è sostenitore: partecipa a eventi riservati ai soci, alle estrazioni mensili e gode di premi migliori nei tornei a cui partecipa.',
    color: C.yellow,
  },
  {
    name: 'Laura',
    role: 'Genitore',
    text: 'Con pochi euro al mese sostiene CIL e in cambio i suoi figli accedono a laboratori e attività a prezzi agevolati, con priorità nelle iscrizioni e inviti a eventi famiglia.',
    color: C.green,
  },
  {
    name: 'Giulia',
    role: 'Creativa',
    text: "Sostenitrice da un anno: usa gli sconti nei negozi convenzionati, partecipa ai workshop riservati e ha vinto materiale in un'estrazione mensile.",
    color: C.blue,
  },
  {
    name: 'Andrea',
    role: 'Passa spesso da CIL',
    text: 'Ha scelto un tier basso: partecipa agli eventi riservati ai soci, alle estrazioni mensili e ha diritto ai vantaggi con i partner convenzionati.',
    color: C.pink,
  },
];

// Helper per ottenere l'icona in base al tipo di sconto (allineato a VantaggiEsclusiviModal)
const getTypeIcon = (type) => {
  const typeIcons = {
    'all-events': FaCalendarAlt,
    events: FaTicketAlt,
    affiliate: FaStore,
    'all-products': FaShoppingBag,
    products: FaTag,
    feature: FaGift,
    'early-access': FaTicketAlt,
    ristorante: FaStore,
    negozio: FaStore,
    attivita: FaGamepad,
    cultura: FaPaintBrush,
    servizi: FaGift,
    default: FaGift,
  };
  return typeIcons[type?.toLowerCase()] || typeIcons.default;
};

// Estrae una breve descrizione dal tier per le card compatte
const getTierShortDescription = (tier) => {
  if (!tier.description) return 'Sostieni CIL con un contributo mensile.';
  if (Array.isArray(tier.description)) {
    const first = tier.description.find(
      (b) => b.type === 'paragraph' && b.children?.length
    );
    if (first?.children) {
      const text =
        first.children
          .map((c) => c.text)
          .filter(Boolean)
          .join(' ') || '';
      return text.slice(0, 100) + (text.length > 100 ? '…' : '');
    }
  }
  return typeof tier.description === 'string'
    ? tier.description.slice(0, 100)
    : 'Sostieni CIL con un contributo mensile.';
};

const API_URL = process.env.REACT_APP_STRAPI_URL || '';
const withBase = (url) =>
  url?.startsWith('http') ? url : API_URL ? `${API_URL}${url || ''}` : url;
const track = (event, payload = {}) => {
  try {
    // eslint-disable-next-line no-console
    console.debug('[track]', event, payload);
  } catch (e) {
    // Silently handle tracking errors
  }
};

const SostieniCIL = () => {
  const { userData } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [goals, setGoals] = useState([]);
  const [loadingTiers, setLoadingTiers] = useState(true);
  const [loadingGoals, setLoadingGoals] = useState(true);
  const [selectedTier, setSelectedTier] = useState(null);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [donationAmount, setDonationAmount] = useState(0);
  const [subClientSecret, setSubClientSecret] = useState('');
  const [subLocalSubscriptionId, setSubLocalSubscriptionId] = useState('');
  const [customAmounts, setCustomAmounts] = useState({});

  // Nuovo state per Donazione Libera
  const [openFreeDonation, setOpenFreeDonation] = useState(false);
  const [freeAmount, setFreeAmount] = useState(20);

  // Nuovo state per il conteggio delle subscription attive
  const [activeSubscriptionsCount, setActiveSubscriptionsCount] = useState(0);

  // State per il modale "Come funziona"
  const [isHowItWorksOpen, setIsHowItWorksOpen] = useState(false);

  // State per il modale dei vantaggi esclusivi
  const [selectedSconto, setSelectedSconto] = useState(null);
  const [isVantaggiModalOpen, setIsVantaggiModalOpen] = useState(false);
  // Filtro per target dei subscription tiers (Mecenate primo, poi Genitori, Magic)
  const [tierTargetFilter, setTierTargetFilter] = useState('Mecenate');

  // State per il carosello hero

  // State per la sezione impatto (sostenitori: 5, 25, 50, 100)

  // ── Load subscription tiers ─────────────────────────────────────────
  useEffect(() => {
    const fetchTiers = async () => {
      try {
        const response = await fetch('/api/subscription-tiers');
        if (response.ok) {
          const data = await response.json();
          const tiersArray = Array.isArray(data)
            ? data
            : data?.data && Array.isArray(data.data)
              ? data.data
              : [];
          setTiers(tiersArray);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Errore nel caricamento dei tier:', error);
        setTiers([]);
      } finally {
        setLoadingTiers(false);
      }
    };

    fetchTiers();
  }, []);

  // ── Load donation goals ────────────────────────────────────────────
  useEffect(() => {
    const fetchGoals = async () => {
      try {
        const response = await fetch('/api/donation-goals');
        if (response.ok) {
          const data = await response.json();
          const goalsArray = Array.isArray(data)
            ? data
            : data?.data && Array.isArray(data.data)
              ? data.data
              : [];
          setGoals(goalsArray);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Errore nel caricamento dei goal:', error);
        setGoals([]);
      } finally {
        setLoadingGoals(false);
      }
    };

    fetchGoals();
  }, []);

  // ── Load total active subscriptions count from server ────────────────
  useEffect(() => {
    const fetchTotalActiveSubs = async () => {
      try {
        // Fetch total active subscriptions count from server
        const response = await fetch('/api/subscriptions/total-active-count');
        if (response.ok) {
          const data = await response.json();
          setActiveSubscriptionsCount(data.count || 0);
        } else {
          // eslint-disable-next-line no-console
          console.warn('Total active subscriptions endpoint not available');
          setActiveSubscriptionsCount(0);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(
          'Errore nel caricamento del conteggio totale subscription:',
          error
        );
        setActiveSubscriptionsCount(0);
      }
    };

    fetchTotalActiveSubs();
  }, []);

  // ── Memoized calculations ──────────────────────────────────────────
  const memoizedTiers = useMemo(() => tiers, [tiers]);
  const tierTargets = useMemo(() => {
    const targets = new Set();
    (tiers || []).forEach((t) => {
      if (t.target) targets.add(t.target);
    });
    const list = Array.from(targets);
    list.sort((a, b) =>
      a === 'Mecenate' ? -1 : b === 'Mecenate' ? 1 : a.localeCompare(b)
    );
    return list;
  }, [tiers]);
  const filteredTiers = useMemo(() => {
    if (!Array.isArray(memoizedTiers)) return [];
    if (tierTargetFilter === 'all') return memoizedTiers;
    return memoizedTiers.filter((t) => t.target === tierTargetFilter);
  }, [memoizedTiers, tierTargetFilter]);
  const memoizedGoals = useMemo(() => {
    if (!Array.isArray(goals) || goals.length === 0) {
      return [];
    }

    // Ordina i gol: prima i non completati, poi i completati
    return [...goals].sort((a, b) => {
      // Calcola se ogni goal è completato
      const aTarget = Number(a.targetAmount) || 0;
      const aDonated =
        Number(a.currentAmount) ||
        (a.donations || [])
          .filter((d) => d?.donationStatus === 'succeeded')
          .reduce((sum, d) => sum + (Number(d?.amount) || 0), 0);
      const aIsCompleted = aDonated >= aTarget && aTarget > 0;

      const bTarget = Number(b.targetAmount) || 0;
      const bDonated =
        Number(b.currentAmount) ||
        (b.donations || [])
          .filter((d) => d?.donationStatus === 'succeeded')
          .reduce((sum, d) => sum + (Number(d?.amount) || 0), 0);
      const bIsCompleted = bDonated >= bTarget && bTarget > 0;

      // I non completati (false) vengono prima dei completati (true)
      // false - true = -1 (a viene prima)
      // true - false = 1 (b viene prima)
      // false - false = 0 (mantieni ordine)
      // true - true = 0 (mantieni ordine)
      return aIsCompleted - bIsCompleted;
    });
  }, [goals]);

  // ── Handle subscription ─────────────────────────────────────────────
  const handleSubscribe = async (tier) => {
    if (!userData) {
      navigate('/login');
      return;
    }
    setSelectedTier(tier);
    try {
      const { data } = await createSubscription({
        userId: userData.id,
        tierId: tier.documentId,
        stripeCustomerId: userData.stripeCustomerId,
        stripePriceId: tier.stripePriceId,
        metadata: {},
      });
      setSubClientSecret(data.clientSecret);
      setSubLocalSubscriptionId(data.localSubscriptionId);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Errore creazione subscription', err);
      const msg = err.response?.data?.error;
      toast({
        title: msg || 'Errore durante la sottoscrizione',
        status: 'error',
      });
    }
  };

  // ── Open donation drawer ───────────────────────────────────────────
  const openDonation = (goal, amount) => {
    setSelectedGoal(goal);
    setDonationAmount(amount);
  };

  const sortedFilteredTiers = [...filteredTiers].sort(
    (a, b) => (Number(a.price) || 0) - (Number(b.price) || 0)
  );
  // Piano evidenziato: il secondo per prezzo (se ce ne sono almeno 3)
  const featuredIndex = sortedFilteredTiers.length >= 3 ? 1 : -1;

  return (
    <Box bg={C.bg} minH="100vh" color="white">
      {/* 1. HERO */}
      <Box
        position="relative"
        overflow="hidden"
        backgroundImage={`url(${SostienicilHeroImage})`}
        backgroundSize="cover"
        backgroundPosition="center"
      >
        <Box
          position="absolute"
          inset={0}
          bg={{
            base: 'linear-gradient(0deg, rgba(14,16,19,0.94) 0%, rgba(14,16,19,0.7) 100%)',
            md: 'linear-gradient(90deg, rgba(14,16,19,0.94) 0%, rgba(14,16,19,0.6) 50%, rgba(14,16,19,0.2) 100%)',
          }}
        />
        <Container
          maxW="1120px"
          position="relative"
          zIndex={1}
          px={{ base: 4, md: 8 }}
          pt={{ base: 12, md: 18 }}
          pb={{ base: 6, md: 18 }}
        >
          <Grid
            templateColumns={{ base: '1fr', lg: '1.2fr 0.8fr' }}
            templateAreas={{
              base: '"text" "card" "extra"',
              lg: '"text card" "extra card"',
            }}
            columnGap={{ lg: 12 }}
            rowGap={{ base: 6, lg: 0 }}
          >
            <Box gridArea="text" alignSelf="end" textAlign="center">
              <Eyebrow>SostieniCIL</Eyebrow>
              <Heading
                as="h1"
                fontSize={{ base: '3xl', md: '5xl', lg: '6xl' }}
                fontWeight="800"
                lineHeight="1.1"
                letterSpacing="-0.02em"
                maxW="none"
                mb={{ base: 0, md: 4 }}
              >
                Costruiamo nuove opportunità, insieme.
              </Heading>
              <Text
                display={{ base: 'none', md: 'block' }}
                fontSize={{ base: 'md', md: 'lg' }}
                color="gray.200"
                maxW="52ch"
                mx="auto"
                mb={8}
              >
                Con una donazione, anche piccola, sostieni eventi di gioco,
                laboratori creativi, attività per ragazzi e progetti educativi.
              </Text>
            </Box>
            <Box gridArea="extra" alignSelf="start">
              {activeSubscriptionsCount > 0 && (
                <HStack
                  justify="center"
                  spacing={2.5}
                  mt={{ base: 0, lg: 6 }}
                  color="gray.400"
                  fontWeight="600"
                >
                  <Box
                    w="8px"
                    h="8px"
                    borderRadius="full"
                    bg={C.green}
                    boxShadow="0 0 0 4px rgba(62,178,151,0.2)"
                  />
                  <Text>
                    <Text as="b" color="white">
                      {activeSubscriptionsCount}
                    </Text>{' '}
                    sostenitori attivi
                  </Text>
                </HStack>
              )}
            </Box>
            <Box
              id="donation-rapida"
              gridArea="card"
              alignSelf="center"
              bg={C.yellow}
              color={C.bg}
              borderRadius="20px"
              p={{ base: 6, md: 8 }}
              boxShadow="0 24px 60px rgba(0,0,0,0.35)"
            >
              <Eyebrow
                color="rgba(22,24,29,0.65)"
                display={{ base: 'none', md: 'block' }}
              >
                Donazione libera
              </Eyebrow>
              <Heading
                as="h2"
                fontSize={{ base: '2xl', md: '3xl' }}
                fontWeight="800"
                letterSpacing="-0.02em"
                mb={{ base: 4, md: 2 }}
              >
                Dona ora
              </Heading>
              <Text
                display={{ base: 'none', md: 'block' }}
                mb={5}
                color="rgba(22,24,29,0.78)"
              >
                Scegli un importo e dona subito. Ogni euro conta per creare
                spazi di creatività e comunità.
              </Text>
              <Grid
                templateColumns="repeat(4, 1fr)"
                p={1}
                mb={4}
                bg="rgba(22,24,29,0.1)"
                borderRadius="8px"
              >
                {[5, 10, 20, 50].map((amount) => {
                  const pressed = freeAmount === amount;
                  return (
                    <Box
                      key={amount}
                      as="button"
                      type="button"
                      aria-label={`Dona ${amount} euro`}
                      aria-pressed={pressed}
                      py={3}
                      borderRadius="6px"
                      fontWeight="800"
                      bg={pressed ? C.bg : 'transparent'}
                      color={pressed ? C.yellow : C.bg}
                      transition="all 0.15s"
                      onClick={() => {
                        setFreeAmount(amount);
                        track('cta_donazione_rapida', { amount });
                      }}
                    >
                      €{amount}
                    </Box>
                  );
                })}
              </Grid>
              <Flex direction={{ base: 'column', sm: 'row' }} gap={2}>
                <NumberInput
                  min={1}
                  flex="1"
                  value={freeAmount}
                  onChange={(val) => setFreeAmount(Number(val) || 0)}
                >
                  <NumberInputField
                    aria-label="Importo"
                    placeholder="Importo"
                    h="48px"
                    bg="rgba(22,24,29,0.1)"
                    border="none"
                    borderRadius="8px"
                    fontWeight="800"
                    _focus={{ boxShadow: `0 0 0 2px ${C.bg}` }}
                  />
                </NumberInput>
                <PrimaryButton
                  bg={C.bg}
                  color={C.yellow}
                  _hover={{ bg: '#000', transform: 'translateY(-1px)' }}
                  isDisabled={!freeAmount || freeAmount <= 0}
                  aria-label="Dona importo personalizzato"
                  onClick={() => {
                    if (freeAmount > 0) {
                      setOpenFreeDonation(true);
                      track('cta_donazione_personalizzata', {
                        amount: freeAmount,
                      });
                    }
                  }}
                >
                  Dona €{freeAmount || 0}
                </PrimaryButton>
              </Flex>
              <Text
                mt={2}
                fontSize="13px"
                fontWeight="600"
                color="rgba(22,24,29,0.7)"
              >
                Pagamento sicuro con carta. Nessuna registrazione richiesta.
              </Text>
            </Box>
          </Grid>
        </Container>
      </Box>

      {/* 2. PIANI */}
      <Section id="subscription-plans" pt={{ base: 4, md: 12 }}>
        <SectionHead
          eyebrow="Sottoscrizione mensile"
          title="Scegli come sostenerci"
        />

        {!loadingTiers && tierTargets.length > 0 && (
          <HStack
            role="tablist"
            spacing={8}
            borderBottom="1px solid"
            borderColor={C.line}
            mb={8}
            overflowX="auto"
            sx={{ scrollbarWidth: 'none' }}
          >
            {tierTargets.map((target) => {
              const isActive = tierTargetFilter === target;
              return (
                <Box
                  key={target}
                  as="button"
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setTierTargetFilter(target)}
                  pb={3.5}
                  mb="-1px"
                  fontSize="md"
                  fontWeight="700"
                  whiteSpace="nowrap"
                  color={isActive ? 'white' : 'gray.400'}
                  borderBottom="2px solid"
                  borderColor={isActive ? C.yellow : 'transparent'}
                  transition="all 0.2s"
                  _hover={{ color: 'white' }}
                >
                  {target}
                </Box>
              );
            })}
          </HStack>
        )}

        {loadingTiers ? (
          <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={6}>
            {[1, 2, 3, 4].map((i) => (
              <Skeleton
                key={i}
                height="320px"
                borderRadius="14px"
                startColor="#252930"
                endColor="#2f343c"
              />
            ))}
          </SimpleGrid>
        ) : sortedFilteredTiers.length === 0 ? (
          <Text color="gray.400" py={8}>
            {tierTargetFilter === 'all'
              ? 'Nessun piano attivo al momento.'
              : 'Nessun piano per questo target.'}
          </Text>
        ) : (
          <Flex
            gap={{ base: 4, lg: 6 }}
            align="stretch"
            overflowX={{ base: 'auto', md: 'visible' }}
            flexWrap={{ base: 'nowrap', md: 'wrap' }}
            mx={{ base: -4, md: 0 }}
            px={{ base: 4, md: 0 }}
            pb={{ base: 2, md: 0 }}
            sx={{
              scrollSnapType: 'x mandatory',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {sortedFilteredTiers.map((tier, idx) => {
              const accent = ACCENTS[idx % ACCENTS.length];
              const featured = idx === featuredIndex;
              const TierButton = featured ? PrimaryButton : GhostButton;
              return (
                <Flex
                  key={tier.documentId}
                  direction="column"
                  flex={{
                    base: '0 0 82%',
                    md: '1 1 calc(50% - 12px)',
                    lg: '1 1 0',
                  }}
                  minW={{ lg: '220px' }}
                  scrollSnapAlign="start"
                  position="relative"
                  p={6}
                  bg={
                    featured
                      ? `linear-gradient(180deg, ${accent}1a, ${C.surface})`
                      : C.surface
                  }
                  border="1px solid"
                  borderColor={featured ? `${accent}73` : C.line}
                  borderTop="3px solid"
                  borderTopColor={accent}
                  borderRadius="14px"
                  transition="transform 0.25s, border-color 0.25s"
                  _hover={{
                    transform: 'translateY(-4px)',
                    borderColor: accent,
                  }}
                >
                  {featured && (
                    <Text
                      position="absolute"
                      top={4}
                      right={4}
                      fontSize="11px"
                      fontWeight="800"
                      letterSpacing="0.08em"
                      textTransform="uppercase"
                      color={accent}
                    >
                      Consigliato
                    </Text>
                  )}
                  <Heading as="h3" fontSize="19px" fontWeight="800" mb={1.5}>
                    {tier.name}
                  </Heading>
                  <Text fontSize="sm" color="gray.400" minH="42px" mb={4}>
                    {getTierShortDescription(tier)}
                  </Text>
                  <Text
                    fontSize="40px"
                    fontWeight="800"
                    letterSpacing="-0.03em"
                    lineHeight={1}
                    mb={4}
                  >
                    €{tier.price}
                    <Text
                      as="span"
                      fontSize="sm"
                      fontWeight="600"
                      color="gray.400"
                      letterSpacing={0}
                    >
                      /mese
                    </Text>
                  </Text>

                  <Box
                    flex={1}
                    pt={4}
                    mb={6}
                    borderTop="1px solid"
                    borderColor={C.line}
                  >
                    {tier.sconti && tier.sconti.length > 0 && (
                      <>
                        <Text
                          fontSize="11px"
                          fontWeight="700"
                          letterSpacing="0.1em"
                          textTransform="uppercase"
                          color="gray.500"
                          mb={1.5}
                        >
                          Vantaggi inclusi
                        </Text>
                        <VStack spacing={0} align="stretch">
                          {tier.sconti.map((sconto, scontoIndex) => (
                            <Flex
                              key={
                                sconto.documentId ||
                                `${tier.documentId}-sconto-${scontoIndex}`
                              }
                              as="button"
                              type="button"
                              align="center"
                              gap={2.5}
                              py={1.5}
                              textAlign="left"
                              fontSize="sm"
                              fontWeight="600"
                              _hover={{ color: C.yellow }}
                              onClick={() => {
                                setSelectedSconto(sconto);
                                setIsVantaggiModalOpen(true);
                              }}
                            >
                              <Icon
                                as={getTypeIcon(sconto.type)}
                                boxSize={3.5}
                                color={accent}
                                flexShrink={0}
                              />
                              <Text flex={1} noOfLines={1}>
                                {sconto.name}
                              </Text>
                              {(sconto.sponsors?.length ?? 0) > 0 && (
                                <Text
                                  as="span"
                                  fontSize="xs"
                                  color="gray.500"
                                  fontWeight="700"
                                >
                                  {sconto.sponsors.length}
                                </Text>
                              )}
                            </Flex>
                          ))}
                        </VStack>
                      </>
                    )}
                  </Box>

                  <TierButton
                    w="full"
                    isDisabled={userData ? !tier.stripePriceId : false}
                    onClick={() => {
                      if (!userData) {
                        navigate('/tesseramento');
                        track('cta_diventa_socio_card', {
                          tierId: tier.documentId,
                        });
                        return;
                      }
                      if (!tier.stripePriceId) return;
                      handleSubscribe(tier);
                      track('cta_sostieni_cil_card', {
                        tierId: tier.documentId,
                      });
                    }}
                  >
                    {userData ? 'Sostieni CIL' : 'Diventa socio'}
                  </TierButton>
                </Flex>
              );
            })}
          </Flex>
        )}
      </Section>

      {/* 3. COME FUNZIONA */}
      <Section flush>
        <SectionHead
          eyebrow="Come funziona"
          title="Tre passaggi, zero pensieri"
        />
        <Grid
          templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }}
          border="1px solid"
          borderColor={C.line}
          borderRadius="14px"
          bg={C.surface}
          overflow="hidden"
        >
          {[
            {
              title: 'Scegli come sostenerci',
              text: 'Scegli il livello di sostegno che senti più tuo e conferma il contributo con la tua carta, in modo semplice e sicuro.',
            },
            {
              title: 'Il tuo sostegno continua',
              text: 'Ogni mese il tuo contributo rinnova da solo il tuo sostegno a CIL. Non devi fare nulla: noi ci prendiamo cura del resto.',
            },
            {
              title: "Gestisci dall'account CIL+",
              text: 'Nella tua area CIL+ trovi il tuo sostegno, puoi sospenderlo o riprenderlo quando vuoi e scaricare ricevute e fatture.',
            },
          ].map((item, i) => (
            <Box
              key={item.title}
              p={{ base: 6, md: 8 }}
              borderRight={{ base: 'none', md: i < 2 ? '1px solid' : 'none' }}
              borderBottom={{ base: i < 2 ? '1px solid' : 'none', md: 'none' }}
              borderColor={C.line}
            >
              <Text
                fontSize="13px"
                fontWeight="800"
                letterSpacing="0.1em"
                color={C.yellow}
                mb={4}
              >
                {String(i + 1).padStart(2, '0')}
              </Text>
              <Heading as="h3" fontSize="lg" fontWeight="800" mb={2}>
                {item.title}
              </Heading>
              <Text fontSize="sm" color="gray.400">
                {item.text}
              </Text>
            </Box>
          ))}
        </Grid>
      </Section>

      {/* 4. COSA POSSIAMO COSTRUIRE INSIEME */}
      <Section flush>
        <SectionHead
          eyebrow="L'impatto"
          title="Cosa possiamo costruire insieme"
          subtitle="Ogni piccolo contributo, sommato agli altri, diventa qualcosa di concreto."
        />
        <Box
          border="1px solid"
          borderColor={C.line}
          borderRadius="14px"
          bg={C.surface}
          overflow="hidden"
        >
          {IMPACT_STEPS.map(({ eq, total, unit, benefit, color }, i) => (
            <Grid
              key={eq}
              templateColumns={{ base: '1fr auto', lg: '200px 200px 1fr' }}
              columnGap={6}
              rowGap={1}
              alignItems="center"
              p={6}
              borderBottom={i < IMPACT_STEPS.length - 1 ? '1px solid' : 'none'}
              borderColor={C.line}
              transition="background 0.2s"
              _hover={{ bg: '#252930' }}
            >
              <Text fontSize="13px" fontWeight="600" color="gray.400">
                {eq}
              </Text>
              <Text
                gridColumn={{ base: 2, lg: 'auto' }}
                gridRow={{ base: '1 / 3', lg: 'auto' }}
                textAlign={{ base: 'right', lg: 'left' }}
                fontSize="2xl"
                fontWeight="800"
                letterSpacing="-0.02em"
                color={color}
              >
                {total}
                {unit && (
                  <Text
                    as="span"
                    fontSize="13px"
                    fontWeight="700"
                    color="gray.400"
                    letterSpacing={0}
                  >
                    {' '}
                    {unit}
                  </Text>
                )}
              </Text>
              <Text gridColumn={{ base: 1, lg: 'auto' }}>{benefit}</Text>
            </Grid>
          ))}
        </Box>
      </Section>

      {/* 5. STORIE */}
      <Section flush>
        <SectionHead eyebrow="Chi ci sostiene" title="Esempi di sostenitori" />
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
          {STORIES.map(({ name, role, text, color }) => (
            <Flex
              key={name}
              gap={4}
              p={6}
              bg={C.surface}
              border="1px solid"
              borderColor={C.line}
              borderRadius="14px"
            >
              <Flex
                w="44px"
                h="44px"
                flexShrink={0}
                borderRadius="full"
                bg={color}
                color={C.bg}
                align="center"
                justify="center"
                fontWeight="800"
              >
                {name[0]}
              </Flex>
              <Box>
                <Heading as="h3" fontSize="md" fontWeight="800">
                  {name}
                </Heading>
                <Text fontSize="13px" fontWeight="600" color="gray.500" mb={2}>
                  {role}
                </Text>
                <Text fontSize="sm" color="gray.400">
                  {text}
                </Text>
              </Box>
            </Flex>
          ))}
        </SimpleGrid>
      </Section>

      {/* 7. PROGETTI */}
      <Section flush id="donation-goals">
        <SectionHead
          eyebrow="Raccolte"
          title="Sostieni i nostri progetti"
          subtitle="Ogni obiettivo è una raccolta per un progetto specifico. Scegli quello che ti appassiona."
        />
        {loadingGoals ? (
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
            {[1, 2, 3].map((i) => (
              <Skeleton
                key={`skeleton-${i}`}
                height="380px"
                borderRadius="14px"
                startColor="#252930"
                endColor="#2f343c"
              />
            ))}
          </SimpleGrid>
        ) : !Array.isArray(memoizedGoals) || memoizedGoals.length === 0 ? (
          <Text color="gray.400">Nuovi obiettivi in arrivo</Text>
        ) : (
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
            {memoizedGoals.map((goal, goalIdx) => {
              const {
                documentId,
                title,
                description,
                targetAmount,
                currentAmount,
                donations = [],
                image,
              } = goal;
              const target = Number(targetAmount) || 0;
              const donated =
                Number(currentAmount) ||
                (donations || [])
                  .filter((d) => d?.donationStatus === 'succeeded')
                  .reduce((sum, d) => sum + (Number(d?.amount) || 0), 0);
              const progress =
                target > 0 ? Math.min(100, (donated / target) * 100) : 0;
              const isCompleted = donated >= target && target > 0;
              const imgSrcRaw =
                image?.[0]?.formats?.medium?.url || image?.[0]?.url;
              const imgSrc = imgSrcRaw
                ? withBase(imgSrcRaw)
                : '/default-goal-image.png';
              const accent = ACCENTS[goalIdx % ACCENTS.length];
              const custom = Number(customAmounts[documentId]) || 0;

              return (
                <Flex
                  key={documentId}
                  direction="column"
                  bg={C.surface}
                  border="1px solid"
                  borderColor={C.line}
                  borderRadius="14px"
                  overflow="hidden"
                  transition="all 0.25s"
                  _hover={{
                    borderColor: 'rgba(255,255,255,0.16)',
                    transform: 'translateY(-3px)',
                  }}
                >
                  <Box
                    position="relative"
                    overflow="hidden"
                    sx={{ aspectRatio: '16 / 9' }}
                    bg={`linear-gradient(150deg, ${accent}a6, #1b1e24)`}
                  >
                    <Image
                      src={imgSrc}
                      alt={title}
                      position="absolute"
                      inset={0}
                      w="100%"
                      h="100%"
                      objectFit="cover"
                      filter={isCompleted ? 'grayscale(60%)' : 'none'}
                    />
                    {isCompleted && (
                      <HStack
                        position="absolute"
                        top={4}
                        left={4}
                        spacing={1.5}
                        h="24px"
                        px={2.5}
                        borderRadius="6px"
                        bg="rgba(22,24,29,0.88)"
                        color={C.green}
                        fontSize="11px"
                        fontWeight="800"
                        letterSpacing="0.06em"
                        textTransform="uppercase"
                      >
                        <Icon as={FaCheck} boxSize="11px" />
                        <span>Completato</span>
                      </HStack>
                    )}
                  </Box>
                  <Flex direction="column" gap={2} p={6} flex={1}>
                    <Heading as="h3" fontSize="lg" fontWeight="800">
                      {title}
                    </Heading>
                    <Text fontSize="sm" color="gray.400" noOfLines={2}>
                      {description}
                    </Text>
                    <Box
                      h="6px"
                      mt={2}
                      borderRadius="full"
                      bg="#252930"
                      overflow="hidden"
                    >
                      <Box
                        h="100%"
                        w={`${progress}%`}
                        bg={isCompleted ? C.green : C.yellow}
                        borderRadius="full"
                      />
                    </Box>
                    <Flex
                      justify="space-between"
                      fontSize="13px"
                      fontWeight="600"
                      color="gray.400"
                    >
                      <span>
                        <Text as="b" color="white" fontSize="15px">
                          €{donated?.toFixed?.(0) ?? '0'}
                        </Text>{' '}
                        raccolti
                      </span>
                      <span>
                        {isCompleted
                          ? 'Obiettivo raggiunto'
                          : `obiettivo €${target?.toFixed?.(0) ?? '0'}`}
                      </span>
                    </Flex>

                    {!isCompleted && (
                      <Flex
                        mt={3}
                        pt={4}
                        borderTop="1px solid"
                        borderColor={C.line}
                        align="center"
                        justify="space-between"
                        gap={3}
                        flexWrap="wrap"
                      >
                        <HStack
                          spacing={0.5}
                          p="3px"
                          bg="#252930"
                          borderRadius="8px"
                        >
                          {[5, 10, 20].map((amt) => (
                            <Box
                              key={amt}
                              as="button"
                              type="button"
                              px={3}
                              py={1.5}
                              borderRadius="6px"
                              fontSize="13px"
                              fontWeight="700"
                              color="gray.400"
                              _hover={{
                                color: 'white',
                                bg: 'rgba(255,255,255,0.06)',
                              }}
                              onClick={() => openDonation(goal, amt)}
                            >
                              €{amt}
                            </Box>
                          ))}
                        </HStack>
                        <HStack spacing={3}>
                          <NumberInput
                            min={1}
                            size="sm"
                            w="76px"
                            value={customAmounts[documentId] || ''}
                            onChange={(val) =>
                              setCustomAmounts((prev) => ({
                                ...prev,
                                [documentId]: val,
                              }))
                            }
                          >
                            <NumberInputField
                              placeholder="Altro"
                              aria-label="Altro importo"
                              bg="#252930"
                              border="none"
                              borderRadius="8px"
                              _focus={{ boxShadow: `0 0 0 1px ${C.yellow}` }}
                            />
                          </NumberInput>
                          {custom > 0 && (
                            <TextLink
                              as="button"
                              type="button"
                              fontSize="sm"
                              onClick={() => {
                                openDonation(goal, custom);
                                track(`cta_dona_goal_${documentId}`, {
                                  amount: custom,
                                });
                              }}
                            >
                              Dona €{custom}
                            </TextLink>
                          )}
                        </HStack>
                      </Flex>
                    )}
                  </Flex>
                </Flex>
              );
            })}
          </SimpleGrid>
        )}
      </Section>

      {/* 8. COSTI */}
      <Section flush>
        <SectionHead
          eyebrow="Trasparenza"
          title="Dove vanno i tuoi soldi"
          subtitle="Il contributo copre affitto, utenze, materiali e assicurazione: ogni euro è un investimento nella comunità."
        />
        <Grid
          templateColumns={{ base: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }}
          border="1px solid"
          borderColor={C.line}
          borderRadius="14px"
          bg={C.surface}
          overflow="hidden"
        >
          {[
            ['500€', 'Affitto / mese'],
            ['200€', 'Bollette / mese'],
            ['35€', 'Internet / mese'],
            ['200€', 'Materiali / mese'],
          ].map(([value, label], i) => (
            <Box
              key={label}
              p={{ base: 4, md: 6 }}
              borderRight={{
                base: i % 2 === 0 ? '1px solid' : 'none',
                md: i < 3 ? '1px solid' : 'none',
              }}
              borderBottom={{ base: i < 2 ? '1px solid' : 'none', md: 'none' }}
              borderColor={C.line}
            >
              <Text
                fontSize={{ base: '2xl', md: '3xl' }}
                fontWeight="800"
                letterSpacing="-0.03em"
                lineHeight={1}
              >
                {value}
              </Text>
              <Text mt={1.5} fontSize="sm" fontWeight="600" color="gray.400">
                {label}
              </Text>
            </Box>
          ))}
        </Grid>
      </Section>

      {/* 9. SPONSOR */}
      <Section flush id="sponsor">
        <Grid
          templateColumns={{ base: '1fr', lg: '1.1fr 1fr' }}
          gap={{ base: 8, lg: 12 }}
          alignItems="center"
          bg={C.surface}
          border="1px solid"
          borderColor={C.line}
          borderRadius="20px"
          p={{ base: 6, md: 12 }}
        >
          <Box>
            <Eyebrow>Per aziende e attività</Eyebrow>
            <Heading
              as="h2"
              fontSize={{ base: '2xl', md: '3xl' }}
              fontWeight="800"
              letterSpacing="-0.02em"
            >
              Siamo sempre alla ricerca di sponsor
            </Heading>
            <Text color="gray.400" mt={2} mb={6} maxW="44ch">
              Visibilità, impatto sociale e partnership. Contattaci per
              collaborare.
            </Text>
            <HStack spacing={8} flexWrap="wrap" rowGap={4}>
              <PrimaryButton
                onClick={() => {
                  track('cta_sponsor_contattaci');
                  window.open(
                    'mailto:info@cilassociazione.it?subject=Richiesta%20Sponsor%20CIL',
                    '_blank'
                  );
                }}
              >
                Contattaci
              </PrimaryButton>
              <TextLink
                as={Link}
                to="/sponsor"
                onClick={() => track('cta_sponsor_scopri_piu')}
              >
                Scopri di più
              </TextLink>
            </HStack>
          </Box>
          <VStack align="stretch" spacing={4}>
            {[
              {
                icon: FaEye,
                title: 'Visibilità',
                desc: 'Brand awareness nella comunità',
                color: C.yellow,
              },
              {
                icon: FaHeart,
                title: 'Impatto sociale',
                desc: 'Progetti per la comunità',
                color: C.green,
              },
              {
                icon: FaHandshake,
                title: 'Partnership',
                desc: 'Collaborazioni esclusive',
                color: C.blue,
              },
            ].map((item) => (
              <Flex key={item.title} gap={4} align="flex-start">
                <Icon as={item.icon} boxSize={5} mt={0.5} color={item.color} />
                <Box>
                  <Text fontWeight="700">{item.title}</Text>
                  <Text fontSize="sm" color="gray.400">
                    {item.desc}
                  </Text>
                </Box>
              </Flex>
            ))}
          </VStack>
        </Grid>
      </Section>

      {/* Subscription Drawer */}
      {selectedTier && (
        <SubscriptionDrawer
          isOpen
          onClose={() => setSelectedTier(null)}
          tier={selectedTier}
          clientSecret={subClientSecret}
          localSubscriptionId={subLocalSubscriptionId}
        />
      )}

      {/* Donation Drawer */}
      {selectedGoal && (
        <DonationDrawer
          isOpen
          onClose={() => {
            setSelectedGoal(null);
            setDonationAmount(0);
          }}
          goal={selectedGoal}
          amount={donationAmount}
        />
      )}

      {/* Donation Drawer per donazione libera */}
      {openFreeDonation && (
        <DonationDrawer
          isOpen
          onClose={() => {
            setOpenFreeDonation(false);
            setFreeAmount(20);
          }}
          goal={null}
          amount={freeAmount || 20}
        />
      )}

      {/* How It Works Modal */}
      <HowItWorksModal
        isOpen={isHowItWorksOpen}
        onClose={() => setIsHowItWorksOpen(false)}
      />

      {/* Vantaggi Esclusivi Modal */}
      <VantaggiEsclusiviModal
        isOpen={isVantaggiModalOpen}
        onClose={() => {
          setIsVantaggiModalOpen(false);
          setSelectedSconto(null);
        }}
        sconto={selectedSconto}
      />
    </Box>
  );
};

export default SostieniCIL;
